declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: (...args: any[]) => void
  }
}

const META_COOKIE_TTL_DAYS = 90
export const META_FBP_COOKIE = '_fbp'
export const META_FBC_COOKIE = '_fbc'
export const META_FBCLID_COOKIE = 'fbclid'

const getMetaPixelId = (): string => {
  return process.env.NEXT_PUBLIC_META_PIXEL_ID ?? '3889357778045454'
}

const getBrowserTimestamp = (): number => {
  return Date.now()
}

const buildFbcFromFbclid = (fbclid: string): string => {
  return `fb.1.${getBrowserTimestamp()}.${fbclid}`
}

export const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined
  }

  for (const cookie of document.cookie.split('; ')) {
    const separatorIndex = cookie.indexOf('=')
    if (separatorIndex === -1) {
      continue
    }
    if (cookie.slice(0, separatorIndex) === name) {
      return decodeURIComponent(cookie.slice(separatorIndex + 1))
    }
  }

  return undefined
}

export const setCookie = (name: string, value: string, days = META_COOKIE_TTL_DAYS): void => {
  if (typeof document === 'undefined') {
    return
  }

  const maxAge = days * 24 * 60 * 60
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`
}

export const ensureMetaBrowserIdCookie = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }

  const existingFbp = getCookie(META_FBP_COOKIE)
  if (existingFbp) {
    return existingFbp
  }

  const randomSegment = window.crypto.getRandomValues(new Uint32Array(1))[0]
  const generatedFbp = `fb.1.${getBrowserTimestamp()}.${randomSegment}`
  setCookie(META_FBP_COOKIE, generatedFbp)
  return generatedFbp
}

export const captureMetaClickIds = (): { fbp?: string, fbc?: string } => {
  if (typeof window === 'undefined') {
    return {}
  }

  const fbp = ensureMetaBrowserIdCookie()
  const params = new URLSearchParams(window.location.search)
  const fbclid = params.get('fbclid') ?? getCookie(META_FBCLID_COOKIE)

  if (!fbclid) {
    return {
      fbp,
      fbc: getCookie(META_FBC_COOKIE)
    }
  }

  const fbc = buildFbcFromFbclid(fbclid)
  setCookie(META_FBCLID_COOKIE, fbclid)
  setCookie(META_FBC_COOKIE, fbc)

  return { fbp, fbc }
}

export const getMetaAttribution = (): { fbp?: string, fbc?: string } => {
  if (typeof window === 'undefined') {
    return {}
  }

  const captured = captureMetaClickIds()

  return {
    fbp: captured.fbp ?? getCookie(META_FBP_COOKIE) ?? ensureMetaBrowserIdCookie(),
    fbc: captured.fbc ?? getCookie(META_FBC_COOKIE)
  }
}

export const isMetaAttributedVisit = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  const captured = captureMetaClickIds()
  return Boolean(
    params.get('fbclid') ||
    captured.fbc ||
    getCookie(META_FBCLID_COOKIE) ||
    getCookie(META_FBC_COOKIE)
  )
}

export const trackMetaEvent = (
  eventName: string,
  parameters?: Record<string, unknown>
): void => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function' || !getMetaPixelId()) {
    return
  }

  console.log('[Meta Pixel] fired', { eventName, parameters })
  window.fbq('track', eventName, parameters)
}
